import type { AnyListQuery } from '@till-erp/platform/lists';
import type { MassEditOperation, MassEditOverride } from './mass-edit-api.ts';

/**
 * Auswahl, mit der „Bearbeiten“ die Massenbearbeitung öffnet (Story 1.21): einzelne Schlüssel oder „alle dieser
 * Ansicht“ (Filter-Beschreibung und Zahl; aufgelöst und eingefroren wird sie erst beim Erstellen der Vorschau).
 * Bis zu einigen tausend Schlüsseln passen nicht in die Adresse; sie liegen im Speicher und – damit Neuladen die
 * Auswahl behält – in `sessionStorage` dieses Tabs. Je Person getrennt (`owner` = Benutzer-ID); Abmelden löscht alle
 * (`clearAllMassEditSelections`).
 */
export type StoredMassEditSelection = ({ kind: 'keys'; keys: string[] } | { kind: 'all'; query: AnyListQuery; total: number; totalExact?: boolean; countThreshold?: number }) & {
  /** „erneut anwenden“ (Story 1.31): Feld-Leiste, Hand-Werte und Abwahl eines alten Laufs vorbelegen. */
  prefill?: {
    operations: MassEditOperation[];
    overrides: MassEditOverride[];
    excludedKeys: string[];
    /** Story 10.14 („Vorschlag übernehmen“): nach dem Vorbelegen einmal sofort die Vorschau erstellen. */
    autoPreview?: boolean;
  };
  /** „Per Satz ändern …“ (Story 2.18): KI-Vorschlag, aus dem Auswahl und Feld-Leiste stammen (KI-Rand, Annahmen). */
  suggestion?: { id: string; sentence: string; assumptions: string[]; confidence: number };
};

const memory = new Map<string, StoredMassEditSelection>();
const PREFIX = 'mass-edit-selection:';
const storageKey = (owner: string, list: string) => `${PREFIX}${owner}:${list}`;

export function rememberMassEditSelection(owner: string, list: string, selection: StoredMassEditSelection): void {
  memory.set(storageKey(owner, list), selection);
  try {
    sessionStorage.setItem(storageKey(owner, list), JSON.stringify(selection));
  } catch {
    // Speicher voll oder gesperrt: die Auswahl gilt dann nur bis zum Neuladen.
  }
}

export function readMassEditSelection(owner: string, list: string): StoredMassEditSelection | null {
  const known = memory.get(storageKey(owner, list));
  if (known) return known;
  try {
    const raw = sessionStorage.getItem(storageKey(owner, list));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredMassEditSelection;
    if (parsed.kind === 'keys' ? Array.isArray(parsed.keys) && parsed.keys.length > 0 : parsed.kind === 'all') {
      memory.set(storageKey(owner, list), parsed);
      return parsed;
    }
  } catch {
    // Unlesbar: keine Auswahl.
  }
  return null;
}

export function clearMassEditSelection(owner: string, list: string): void {
  memory.delete(storageKey(owner, list));
  try {
    sessionStorage.removeItem(storageKey(owner, list));
  } catch {
    // nichts
  }
}

export function selectionSize(selection: StoredMassEditSelection): number {
  return selection.kind === 'keys' ? selection.keys.length : selection.total;
}

/** Alle gemerkten Auswahlen aller Personen vergessen (Abmelden). */
export function clearAllMassEditSelections(): void {
  memory.clear();
  try {
    for (const key of Object.keys(sessionStorage)) if (key.startsWith(PREFIX)) sessionStorage.removeItem(key);
  } catch {
    // nichts
  }
}
